/**
 * ActionCard component
 * Generic card with icon + label + onPress. Used by InviteCard and AddGroupCard for consistent styling.
 */

import React, { useMemo } from 'react'
import { Pressable, Text, View, Platform } from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import type { LucideIcon } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { getActionCardStyles, FRIEND_CARD_ASPECT_RATIO } from './ActionCard.styles'

export type ActionCardIconName = LucideIcon

interface ActionCardProps {
  iconName: ActionCardIconName
  label: string
  onPress: () => void
  /** Override the default aspect ratio so the card matches sibling cards in its grid (e.g. taller group cards). */
  aspectRatio?: number
  /**
   * Pin the card to an exact height, for grids whose cards aren't a fixed aspect ratio
   * (e.g. the create flow's RivalCard, whose image height is capped). Wins over `aspectRatio`.
   */
  height?: number
  testID?: string
}

export const ActionCard: React.FC<ActionCardProps> = ({
  iconName: IconComponent,
  label,
  onPress,
  aspectRatio,
  height,
  testID = 'action-card',
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getActionCardStyles(theme), [theme])
  const sizeStyle = useMemo(
    () => (height != null ? { height } : { aspectRatio: aspectRatio ?? FRIEND_CARD_ASPECT_RATIO }),
    [height, aspectRatio]
  )

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.card,
        sizeStyle,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.cardPressed,
        hovered && Platform.OS === 'web' && styles.cardHover,
      ]}
      onPress={onPress}
      testID={testID}
    >
      <View style={styles.iconWrap}>
        <IconComponent size={40} color={theme.primary} />
      </View>
      <Text style={styles.label}>{label}</Text>
    </Pressable>
  )
}
